import { useVirtualizer } from '@tanstack/react-virtual'
import { useEffect, useRef } from 'react'

import type { FindingSummary } from '../api/types'
import { Severity } from './Severity'

/**
 * The virtualised list itself.
 *
 * Virtualised because a queue that renders four thousand rows stutters on every
 * keystroke, and this list is meant to be driven with `j` and `k` held down.
 */
export function QueueList({
  rows,
  cursor,
  selected,
  onCursor,
  onOpen,
}: {
  rows: FindingSummary[]
  cursor: number
  selected: Set<string>
  onCursor: (index: number) => void
  onOpen: (finding: FindingSummary) => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)

  const virtualiser = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 46,
    overscan: 12,
    // A starting viewport, so the first paint is a list rather than an empty box
    // waiting for a ResizeObserver callback.
    initialRect: { width: 1200, height: 600 },
  })

  useEffect(() => {
    virtualiser.scrollToIndex(cursor, { align: 'auto' })
  }, [cursor, virtualiser])

  return (
    <div
      ref={scrollRef}
      role="listbox"
      aria-label="Open findings"
      aria-multiselectable="true"
      tabIndex={0}
      style={{ height: '60vh', overflow: 'auto', border: '1px solid var(--border)' }}
    >
      <div style={{ height: virtualiser.getTotalSize(), position: 'relative' }}>
        {virtualiser.getVirtualItems().map((item) => {
          const finding = rows[item.index]
          if (!finding) return null
          const isSelected = selected.has(finding.id)
          return (
            <div
              key={finding.id}
              role="option"
              aria-selected={item.index === cursor}
              onClick={() => onCursor(item.index)}
              onDoubleClick={() => onOpen(finding)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: item.size,
                transform: `translateY(${item.start}px)`,
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: '0 var(--space-3)',
                background: item.index === cursor ? 'var(--surface-3)' : 'transparent',
                borderLeft: isSelected
                  ? '3px solid var(--accent)'
                  : '3px solid transparent',
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
              }}
            >
              {/* Selection is a word as well as a stripe, for the same reason severity
                  is: a mark nobody can see is not a mark. */}
              {isSelected && <span className="badge">picked</span>}
              <Severity id={finding.severity_id} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {finding.rule_title}
              </span>
              <span className="mono muted">{finding.hostname}</span>
              {finding.occurrence_count > 1 && (
                <span className="badge" title="Occurrences grouped into this alert">
                  ×{finding.occurrence_count}
                </span>
              )}
              {finding.assigned_to && <span className="muted">{finding.assigned_to}</span>}
              <time className="muted mono" dateTime={finding.last_seen}>
                {new Date(finding.last_seen).toLocaleTimeString()}
              </time>
            </div>
          )
        })}
      </div>
    </div>
  )
}
